import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, ShoppingBag,
  Star, RefreshCw, Users, CreditCard, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const COLORS = ['#9333ea', '#ec4899', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

// ─── Helpers ────────────────────────────────────────────────────────────────

function growthPct(current, previous) {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous * 100);
}

function GrowthBadge({ current, previous, suffix = '' }) {
  const p = growthPct(current, previous);
  if (p === null) return <span className="text-xs text-gray-300 font-semibold">—</span>;
  const up = p >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full ${up ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      {up ? '↑' : '↓'} {Math.abs(p).toFixed(1)}%{suffix}
    </span>
  );
}

function KPICard({ title, value, sub, icon: Icon, color, current, previous }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <p className="text-2xl font-extrabold text-gray-900">{value}</p>
        {current !== undefined && <GrowthBadge current={current} previous={previous} />}
      </div>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function SnapshotCard({ label, prevLabel, revenue, count, prevRevenue, prevCount }) {
  const p = growthPct(revenue, prevRevenue);
  const up = p === null ? null : p >= 0;
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        {p !== null && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full ${up ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {up ? '↑' : '↓'} {Math.abs(p).toFixed(1)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-extrabold text-gray-900">€{revenue.toFixed(2)}</p>
      <p className="text-xs text-gray-400 mt-1">{count} encomenda{count !== 1 ? 's' : ''}</p>
      <p className="text-xs text-gray-300 mt-1.5">{prevLabel}: €{prevRevenue.toFixed(2)} · {prevCount} enc.</p>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-lg text-sm">
        <p className="font-bold text-gray-700 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="font-semibold">
            {p.name}: {p.name === 'Receita' || p.name === 'value' ? `€${Number(p.value).toFixed(2)}` : p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ─── Main Component ─────────────────────────────────────────────────────────

export default function AdminStats() {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  const now = new Date();
  const [monthYear, setMonthYear] = useState({ year: now.getFullYear(), month: now.getMonth() });

  function shiftMonth(delta) {
    setMonthYear(prev => {
      let m = prev.month + delta;
      let y = prev.year;
      if (m < 0) { m = 11; y--; }
      if (m > 11) { m = 0; y++; }
      return { year: y, month: m };
    });
  }

  const MONTHS_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  useEffect(() => {
    if (!token) { navigate('/backoffice'); return; }
    axios.get(`${API_URL}/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setOrders(res.data.data))
      .catch(e => { if (e.response?.status === 401) { localStorage.removeItem('adminToken'); navigate('/backoffice'); } })
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const days = parseInt(period);
    const MS_DAY = 86400000;

    const allPaid = orders.filter(o => o.paymentStatus === 'paid');

    // ─── Modo Mês ────────────────────────────────────────────────────────────
    if (period === 'month') {
      const { year, month } = monthYear;
      const startM = new Date(year, month, 1);
      const endM = new Date(year, month + 1, 0, 23, 59, 59, 999);
      const startPrevM = new Date(year, month - 1, 1);
      const endPrevM = new Date(year, month, 0, 23, 59, 59, 999);

      const inM = o => { const d = new Date(o.createdAt); return d >= startM && d <= endM; };
      const inPrevM = o => { const d = new Date(o.createdAt); return d >= startPrevM && d <= endPrevM; };

      const mOrders = orders.filter(inM);
      const mPaid = allPaid.filter(inM);
      const prevMPaid = allPaid.filter(inPrevM);

      const mRevenue = mPaid.reduce((s, o) => s + parseFloat(o.price), 0);
      const prevMRevenue = prevMPaid.reduce((s, o) => s + parseFloat(o.price), 0);
      const mAvg = mPaid.length ? mRevenue / mPaid.length : 0;
      const prevMAvg = prevMPaid.length ? prevMRevenue / prevMPaid.length : 0;

      // Receita por dia (dias decorridos no mês, ou total se mês passado)
      const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
      const daysElapsed = isCurrentMonth ? now.getDate() : new Date(year, month + 1, 0).getDate();
      const prevDaysInMonth = new Date(year, month, 0).getDate();
      const mRevPerDay = daysElapsed ? mRevenue / daysElapsed : 0;
      const prevMRevPerDay = prevDaysInMonth ? prevMRevenue / prevDaysInMonth : 0;

      // Breakdown por método de pagamento
      const methodBreakdownMap = {};
      mPaid.forEach(o => {
        const m = o.paymentMethod === 'mbway' ? 'MB Way' : o.paymentMethod === 'multibanco' ? 'Multibanco' : o.paymentMethod === 'card' ? 'Cartão' : (o.paymentMethod || 'Outro');
        if (!methodBreakdownMap[m]) methodBreakdownMap[m] = { count: 0, revenue: 0 };
        methodBreakdownMap[m].count++;
        methodBreakdownMap[m].revenue += parseFloat(o.price);
      });
      const methodBreakdown = Object.entries(methodBreakdownMap).map(([name, v]) => ({ name, ...v }));

      // Clientes únicos + novos vs recorrentes
      // recorrente = tem 2+ encomendas pagas no total (all-time)
      const mUnique = new Set(mPaid.map(o => o.customerEmail.toLowerCase())).size;
      const prevMUnique = new Set(prevMPaid.map(o => o.customerEmail.toLowerCase())).size;
      let mNewCustomers = 0, mRepeatCustomers = 0, mNewRevenue = 0, mRepeatRevenue = 0;
      const mEmails = [...new Set(mPaid.map(o => o.customerEmail.toLowerCase()))];
      mEmails.forEach(email => {
        const totalOrders = allPaid.filter(o => o.customerEmail.toLowerCase() === email).length;
        const emailRevenue = mPaid.filter(o => o.customerEmail.toLowerCase() === email).reduce((s, o) => s + parseFloat(o.price), 0);
        if (totalOrders > 1) { mRepeatCustomers++; mRepeatRevenue += emailRevenue; }
        else { mNewCustomers++; mNewRevenue += emailRevenue; }
      });
      const mCustomerPieData = [
        { name: 'Novos', value: mNewCustomers },
        { name: 'Recorrentes', value: mRepeatCustomers },
      ].filter(d => d.value > 0);

      // Dia a dia no mês
      const daysInMonth = endM.getDate();
      const revenueChartData = Array.from({ length: daysInMonth }, (_, i) => {
        const day = i + 1;
        const key = `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}`;
        const val = mPaid
          .filter(o => new Date(o.createdAt).getDate() === day)
          .reduce((s, o) => s + parseFloat(o.price), 0);
        return { date: key, Receita: parseFloat(val.toFixed(2)) };
      });

      // Método pagamento (para gráfico)
      const methodData = methodBreakdown.map(m => ({ name: m.name, value: m.count, revenue: m.revenue }));

      // Plataformas
      const platformMap = {};
      mPaid.forEach(o => {
        if (o.items && Array.isArray(o.items)) {
          o.items.forEach(item => { const p = item.platform || 'outro'; platformMap[p] = (platformMap[p] || 0) + parseFloat(item.price || 0); });
        } else if (o.platform) { platformMap[o.platform] = (platformMap[o.platform] || 0) + parseFloat(o.price); }
      });
      const platformData = Object.entries(platformMap)
        .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value: parseFloat(value.toFixed(2)) }))
        .sort((a, b) => b.value - a.value);

      // Top clientes do mês
      const cMap = {};
      mPaid.forEach(o => {
        const key = o.customerEmail.toLowerCase();
        if (!cMap[key]) cMap[key] = { name: o.customerName, email: o.customerEmail, total: 0, count: 0 };
        cMap[key].total += parseFloat(o.price);
        cMap[key].count++;
      });
      const topCustomers = Object.values(cMap).sort((a, b) => b.total - a.total).slice(0, 8);

      // Pacotes do mês
      const pkgMap = {};
      mPaid.forEach(o => {
        if (o.items && Array.isArray(o.items)) {
          o.items.forEach(item => {
            const svc = item.serviceType === 'likes' ? 'Likes' : item.serviceType === 'views' ? 'Views' : 'Seguidores';
            const key = `${item.platform} · ${item.quantity?.toLocaleString('fr-FR')} ${svc}`;
            pkgMap[key] = (pkgMap[key] || 0) + 1;
          });
        } else if (o.platform) {
          const key = `${o.platform} · ${o.packageType || o.followersCount?.toLocaleString('fr-FR')}`;
          pkgMap[key] = (pkgMap[key] || 0) + 1;
        }
      });
      const topPackages = Object.entries(pkgMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 6);

      const stuckOrders = orders.filter(o => o.paymentStatus !== 'pending' ? false : (Date.now() - new Date(o.createdAt).getTime()) / 3600000 > 24);

      // Totais all-time para o banner
      const totalRevenue = allPaid.reduce((s, o) => s + parseFloat(o.price), 0);

      return {
        isMonthMode: true,
        totalRevenue, paidCount: allPaid.length, totalOrders: orders.length,
        allTimeUniqueCustomers: new Set(allPaid.map(o => o.customerEmail.toLowerCase())).size,
        // mês
        periodRevenue: mRevenue, prevRevenue: prevMRevenue,
        periodPaidCount: mPaid.length, prevPaidCount: prevMPaid.length,
        periodOrdersCount: mPaid.length,
        periodAvg: mAvg, prevAvg: prevMAvg,
        mRevPerDay, prevMRevPerDay,
        uniqueCustomers: mUnique, prevUniqueCustomers: prevMUnique,
        mNewCustomers, mRepeatCustomers,
        // mbway/multibanco breakdown
        methodBreakdown,
        // gráfico
        revenueChartData,
        methodData, platformData,
        topCustomers, topPackages,
        stuckOrders,
        // snapshot vazio (não se aplica no modo mês)
        snapshot: null,
        customerPieData: mCustomerPieData, newCustomers: mNewCustomers, repeatCustomers: mRepeatCustomers, newRevenue: mNewRevenue, repeatRevenue: mRepeatRevenue,
        dayData: (() => {
          const days_pt = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
          const dm = { Dom: 0, Seg: 0, Ter: 0, Qua: 0, Qui: 0, Sex: 0, Sáb: 0 };
          mPaid.forEach(o => { dm[days_pt[new Date(o.createdAt).getDay()]]++; });
          return Object.entries(dm).map(([name, value]) => ({ name, value }));
        })(),
      };
    }

    // ─── Período atual vs anterior (para KPIs) ───────────────────────────────
    const cutoff = period === 'all' ? new Date(0) : new Date(now.getTime() - days * MS_DAY);
    const prevCutoff = period === 'all' ? null : new Date(cutoff.getTime() - days * MS_DAY);

    const periodOrders = period === 'all' ? orders : orders.filter(o => new Date(o.createdAt) >= cutoff);
    const periodPaid = periodOrders.filter(o => o.paymentStatus === 'paid');

    const prevOrders = (!prevCutoff) ? [] : orders.filter(o => {
      const d = new Date(o.createdAt);
      return d >= prevCutoff && d < cutoff;
    });
    const prevPaid = prevOrders.filter(o => o.paymentStatus === 'paid');

    // ─── KPIs período ────────────────────────────────────────────────────────
    const totalRevenue = allPaid.reduce((s, o) => s + parseFloat(o.price), 0);
    const periodRevenue = periodPaid.reduce((s, o) => s + parseFloat(o.price), 0);
    const prevRevenue = prevPaid.reduce((s, o) => s + parseFloat(o.price), 0);

    const periodAvg = periodPaid.length ? periodRevenue / periodPaid.length : 0;
    const prevAvg = prevPaid.length ? prevPaid.reduce((s, o) => s + parseFloat(o.price), 0) / prevPaid.length : 0;

    const uniqueCustomers = new Set(periodPaid.map(o => o.customerEmail.toLowerCase())).size;
    const prevUniqueCustomers = new Set(prevPaid.map(o => o.customerEmail.toLowerCase())).size;

    // Receita por dia
    let revPerDay, prevRevPerDay;
    if (period === 'all') {
      const firstOrder = allPaid.length ? new Date(allPaid.reduce((min, o) => new Date(o.createdAt) < min ? new Date(o.createdAt) : min, new Date())) : now;
      const totalDays = Math.max(1, Math.ceil((now - firstOrder) / MS_DAY));
      const prevDays = totalDays; // não há "período anterior" real para all
      revPerDay = totalRevenue / totalDays;
      prevRevPerDay = 0;
    } else {
      revPerDay = days ? periodRevenue / days : 0;
      prevRevPerDay = days ? prevRevenue / days : 0;
    }

    // ─── Snapshot fixo (Hoje / Esta semana / Este mês) ──────────────────────
    const todayStr = now.toDateString();
    const yesterdayStr = new Date(now.getTime() - MS_DAY).toDateString();

    // Semana atual: segunda-feira desta semana
    const dow = now.getDay(); // 0=Dom
    const daysToMon = dow === 0 ? 6 : dow - 1;
    const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - daysToMon); startOfWeek.setHours(0, 0, 0, 0);
    const startOfPrevWeek = new Date(startOfWeek.getTime() - 7 * MS_DAY);

    // Mês atual
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);

    const inRange = (o, from, to) => { const d = new Date(o.createdAt); return d >= from && (!to || d <= to); };

    const snapshot = {
      today: {
        revenue: allPaid.filter(o => new Date(o.createdAt).toDateString() === todayStr).reduce((s, o) => s + parseFloat(o.price), 0),
        count: allPaid.filter(o => new Date(o.createdAt).toDateString() === todayStr).length,
        prevRevenue: allPaid.filter(o => new Date(o.createdAt).toDateString() === yesterdayStr).reduce((s, o) => s + parseFloat(o.price), 0),
        prevCount: allPaid.filter(o => new Date(o.createdAt).toDateString() === yesterdayStr).length,
        label: 'Hoje', prevLabel: 'Ontem',
      },
      week: {
        revenue: allPaid.filter(o => inRange(o, startOfWeek)).reduce((s, o) => s + parseFloat(o.price), 0),
        count: allPaid.filter(o => inRange(o, startOfWeek)).length,
        prevRevenue: allPaid.filter(o => inRange(o, startOfPrevWeek, new Date(startOfWeek.getTime() - 1))).reduce((s, o) => s + parseFloat(o.price), 0),
        prevCount: allPaid.filter(o => inRange(o, startOfPrevWeek, new Date(startOfWeek.getTime() - 1))).length,
        label: 'Esta Semana', prevLabel: 'Semana passada',
      },
      month: {
        revenue: allPaid.filter(o => inRange(o, startOfMonth)).reduce((s, o) => s + parseFloat(o.price), 0),
        count: allPaid.filter(o => inRange(o, startOfMonth)).length,
        prevRevenue: allPaid.filter(o => inRange(o, startOfPrevMonth, endOfPrevMonth)).reduce((s, o) => s + parseFloat(o.price), 0),
        prevCount: allPaid.filter(o => inRange(o, startOfPrevMonth, endOfPrevMonth)).length,
        label: 'Este Mês', prevLabel: 'Mês passado',
      },
    };

    // ─── Gráfico receita ao longo do tempo ──────────────────────────────────
    let revenueChartData;
    if (period === 'all') {
      const revenueByMonth = {};
      allPaid.forEach(o => {
        const key = new Date(o.createdAt).toLocaleDateString('pt-PT', { month: '2-digit', year: '2-digit' });
        revenueByMonth[key] = (revenueByMonth[key] || 0) + parseFloat(o.price);
      });
      revenueChartData = Object.entries(revenueByMonth)
        .sort((a, b) => {
          const [am, ay] = a[0].split('/');
          const [bm, by] = b[0].split('/');
          return (parseInt(ay) * 12 + parseInt(am)) - (parseInt(by) * 12 + parseInt(bm));
        })
        .map(([date, value]) => ({ date, Receita: parseFloat(value.toFixed(2)) }));
    } else {
      const revenueByDay = {};
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(now.getTime() - i * MS_DAY);
        const key = d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
        revenueByDay[key] = 0;
      }
      periodPaid.forEach(o => {
        const key = new Date(o.createdAt).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
        if (revenueByDay[key] !== undefined) revenueByDay[key] += parseFloat(o.price);
      });
      revenueChartData = Object.entries(revenueByDay).map(([date, value]) => ({
        date, Receita: parseFloat(value.toFixed(2)),
      }));
    }

    // ─── Novos vs Recorrentes (período) ─────────────────────────────────────
    // novo = primeira compra foi neste período (sem pedidos antes do cutoff)
    // recorrente = já tinha pelo menos um pedido pago antes do cutoff
    // excepção: period=all → recorrente = 2+ pedidos no total
    const customerMap = {};
    [...allPaid].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).forEach(o => {
      const key = o.customerEmail.toLowerCase();
      if (!customerMap[key]) customerMap[key] = { orders: [], name: o.customerName };
      customerMap[key].orders.push(o);
    });
    const customers = Object.values(customerMap);
    let newCustomers = 0, repeatCustomers = 0, newRevenue = 0, repeatRevenue = 0;
    customers.forEach(c => {
      const ordersInPeriod = c.orders.filter(o => new Date(o.createdAt) >= cutoff);
      if (ordersInPeriod.length === 0) return;
      const isRepeat = c.orders.length > 1;
      if (isRepeat) {
        repeatCustomers++;
        repeatRevenue += ordersInPeriod.reduce((s, o) => s + parseFloat(o.price), 0);
      } else {
        newCustomers++;
        newRevenue += ordersInPeriod.reduce((s, o) => s + parseFloat(o.price), 0);
      }
    });
    const customerPieData = [
      { name: 'Novos', value: newCustomers },
      { name: 'Recorrentes', value: repeatCustomers },
    ].filter(d => d.value > 0);

    // ─── Top clientes (all time) ─────────────────────────────────────────────
    const topCustomers = customers
      .map(c => ({
        name: c.name,
        email: c.orders[0].customerEmail,
        total: c.orders.reduce((s, o) => s + parseFloat(o.price), 0),
        count: c.orders.length,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);

    // ─── Pacotes mais vendidos (all time) ────────────────────────────────────
    const packageMap = {};
    allPaid.forEach(o => {
      if (o.items && Array.isArray(o.items)) {
        o.items.forEach(item => {
          const svc = item.serviceType === 'likes' ? 'Likes' : item.serviceType === 'views' ? 'Views' : 'Seguidores';
          const key = `${item.platform} · ${item.quantity?.toLocaleString('fr-FR')} ${svc}`;
          packageMap[key] = (packageMap[key] || 0) + 1;
        });
      } else if (o.platform) {
        const key = `${o.platform} · ${o.packageType || o.followersCount?.toLocaleString('fr-FR')}`;
        packageMap[key] = (packageMap[key] || 0) + 1;
      }
    });
    const topPackages = Object.entries(packageMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);

    // ─── Receita por plataforma (all time) ───────────────────────────────────
    const platformMap = {};
    allPaid.forEach(o => {
      if (o.items && Array.isArray(o.items)) {
        o.items.forEach(item => {
          const p = item.platform || 'outro';
          platformMap[p] = (platformMap[p] || 0) + parseFloat(item.price || 0);
        });
      } else if (o.platform) {
        platformMap[o.platform] = (platformMap[o.platform] || 0) + parseFloat(o.price);
      }
    });
    const platformData = Object.entries(platformMap)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value: parseFloat(value.toFixed(2)) }))
      .sort((a, b) => b.value - a.value);

    // ─── Vendas por dia da semana (all time) ─────────────────────────────────
    const days_pt = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const dayMap = { Dom: 0, Seg: 0, Ter: 0, Qua: 0, Qui: 0, Sex: 0, Sáb: 0 };
    allPaid.forEach(o => { dayMap[days_pt[new Date(o.createdAt).getDay()]]++; });
    const dayData = Object.entries(dayMap).map(([name, value]) => ({ name, value }));

    // ─── Método de pagamento (período) ───────────────────────────────────────
    const methodBreakdownMap = {};
    periodPaid.forEach(o => {
      const m = o.paymentMethod === 'mbway' ? 'MB Way' : o.paymentMethod === 'multibanco' ? 'Multibanco' : o.paymentMethod === 'card' ? 'Cartão' : (o.paymentMethod || 'Outro');
      if (!methodBreakdownMap[m]) methodBreakdownMap[m] = { count: 0, revenue: 0 };
      methodBreakdownMap[m].count++;
      methodBreakdownMap[m].revenue += parseFloat(o.price);
    });
    const methodBreakdown = Object.entries(methodBreakdownMap).map(([name, v]) => ({ name, ...v }));
    const methodData = methodBreakdown.map(m => ({ name: m.name, value: m.count, revenue: m.revenue }));

    // ─── Pendentes há +24h ───────────────────────────────────────────────────
    const stuckOrders = orders.filter(o => {
      if (o.paymentStatus !== 'pending') return false;
      return (Date.now() - new Date(o.createdAt).getTime()) / 3600000 > 24;
    });

    return {
      isMonthMode: false,
      // totais all-time
      totalRevenue, paidCount: allPaid.length, totalOrders: orders.length,
      allTimeUniqueCustomers: customers.length,
      // período atual
      periodRevenue, periodPaidCount: periodPaid.length, periodOrdersCount: periodOrders.length,
      periodAvg, uniqueCustomers,
      // período anterior (para comparação)
      prevRevenue, prevPaidCount: prevPaid.length, prevAvg, prevUniqueCustomers,
      // gráfico
      revenueChartData,
      // snapshot
      snapshot,
      // clientes
      customerPieData, newCustomers, repeatCustomers, newRevenue, repeatRevenue,
      topCustomers,
      // produtos
      topPackages, platformData,
      // outros
      dayData, methodData, stuckOrders,
      methodBreakdown,
      mRevPerDay: revPerDay, prevMRevPerDay: prevRevPerDay, mNewCustomers: 0, mRepeatCustomers: 0,
    };
  }, [orders, period, monthYear]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">A calcular estatísticas...</div>
      </div>
    );
  }

  const periodLabel = period === 'month'
    ? `${MONTHS_PT[monthYear.month]} ${monthYear.year}`
    : period === '7' ? 'últimos 7 dias' : period === '30' ? 'últimos 30 dias' : period === '90' ? 'últimos 3 meses' : 'sempre';
  const xAxisInterval = period === '7' ? 0 : period === '30' ? 4 : period === 'all' ? 0 : 6;

  return (
    <div className="min-h-screen bg-gray-50">

      <AdminHeader activePage="stats" />

      <div className="container mx-auto px-4 py-6 space-y-6">

        {/* Filtro de período */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-400 font-medium">Período:</span>
          {[['7', '7 dias'], ['30', '30 dias'], ['90', '3 meses'], ['month', 'Por Mês'], ['all', 'Tudo']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setPeriod(val)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${period === val ? 'bg-purple-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {label}
            </button>
          ))}
          {period === 'month' && (
            <div className="flex items-center gap-1 ml-2 bg-white border border-gray-200 rounded-xl px-2 py-1 shadow-sm">
              <button onClick={() => shiftMonth(-1)} className="p-1 rounded-lg hover:bg-gray-100 transition">
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <span className="text-sm font-bold text-gray-800 px-2 min-w-[130px] text-center">
                {MONTHS_PT[monthYear.month]} {monthYear.year}
              </span>
              <button
                onClick={() => shiftMonth(1)}
                disabled={monthYear.year === now.getFullYear() && monthYear.month >= now.getMonth()}
                className="p-1 rounded-lg hover:bg-gray-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          )}
        </div>


        {/* ── Snapshot sempre atualizado (não aparece no modo mês) ── */}
        {!stats.isMonthMode && (
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Snapshot — sempre atualizado</p>
            <div className="grid grid-cols-3 gap-4">
              <SnapshotCard {...stats.snapshot.today} />
              <SnapshotCard {...stats.snapshot.week} />
              <SnapshotCard {...stats.snapshot.month} />
            </div>
          </div>
        )}

        {/* ── KPIs do período ── */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
            KPIs — {periodLabel}
            {period !== 'all' && <span className="text-gray-300 font-normal ml-1">(seta compara com período anterior igual)</span>}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KPICard
              title="Receita"
              value={`€${stats.periodRevenue.toFixed(2)}`}
              sub={`${stats.periodPaidCount} encomendas pagas`}
              icon={TrendingUp} color="bg-purple-500"
              current={stats.periodRevenue} previous={stats.prevRevenue}
            />
            <KPICard
              title="Encomendas"
              value={stats.periodPaidCount}
              sub="encomendas pagas no período"
              icon={ShoppingBag} color="bg-pink-500"
              current={stats.periodPaidCount} previous={stats.prevPaidCount}
            />
            <KPICard
              title="Receita / Dia"
              value={`€${stats.mRevPerDay.toFixed(2)}`}
              sub={stats.isMonthMode ? 'média diária do mês' : period === 'all' ? 'média desde o início' : `média nos últimos ${period} dias`}
              icon={RefreshCw} color="bg-blue-500"
              current={stats.mRevPerDay} previous={stats.prevMRevPerDay}
            />
            <KPICard
              title="Ticket Médio"
              value={`€${stats.periodAvg.toFixed(2)}`}
              sub="por encomenda paga"
              icon={CreditCard} color="bg-green-500"
              current={stats.periodAvg} previous={stats.prevAvg}
            />
            <KPICard
              title="Clientes Únicos"
              value={stats.uniqueCustomers}
              sub={`${stats.isMonthMode ? stats.mNewCustomers : stats.newCustomers} novos · ${stats.isMonthMode ? stats.mRepeatCustomers : stats.repeatCustomers} recorrentes`}
              icon={Users} color="bg-orange-500"
              current={stats.uniqueCustomers} previous={stats.prevUniqueCustomers}
            />
          </div>
        </div>

        {/* ── Breakdown por método de pagamento ── */}
        {stats.methodBreakdown.length > 0 && (
          <div className={`grid gap-4 ${stats.methodBreakdown.length === 1 ? 'grid-cols-1' : stats.methodBreakdown.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {stats.methodBreakdown.map((m, i) => {
              const colors = ['text-purple-700', 'text-blue-700', 'text-green-700', 'text-pink-700'];
              return (
                <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{m.name}</p>
                  <p className={`text-3xl font-extrabold ${colors[i % colors.length]}`}>€{m.revenue.toFixed(2)}</p>
                  <p className="text-sm text-gray-400 mt-1">{m.count} pagamento{m.count !== 1 ? 's' : ''}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Totais all-time (contextual) ── */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 rounded-xl p-4 flex flex-wrap items-center gap-6">
          <div>
            <p className="text-xs text-purple-500 font-semibold uppercase tracking-wide">Receita Total (tudo)</p>
            <p className="text-2xl font-extrabold text-purple-800">€{stats.totalRevenue.toFixed(2)}</p>
          </div>
          <div className="w-px h-10 bg-purple-200 hidden sm:block" />
          <div>
            <p className="text-xs text-purple-500 font-semibold uppercase tracking-wide">Encomendas Pagas</p>
            <p className="text-2xl font-extrabold text-purple-800">{stats.paidCount}</p>
          </div>
          <div className="w-px h-10 bg-purple-200 hidden sm:block" />
          <div>
            <p className="text-xs text-purple-500 font-semibold uppercase tracking-wide">Total Criadas</p>
            <p className="text-2xl font-extrabold text-purple-800">{stats.totalOrders}</p>
          </div>
          <div className="w-px h-10 bg-purple-200 hidden sm:block" />
          <div>
            <p className="text-xs text-purple-500 font-semibold uppercase tracking-wide">Clientes Únicos</p>
            <p className="text-2xl font-extrabold text-purple-800">{stats.allTimeUniqueCustomers}</p>
          </div>
        </div>

        {/* ── Gráfico receita ao longo do tempo ── */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <p className="font-bold text-gray-900 mb-1">Receita ao Longo do Tempo</p>
          <p className="text-xs text-gray-400 mb-5">
            {period === 'all' ? 'Agrupado por mês' : `Dia a dia — ${periodLabel}`}
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.revenueChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} interval={xAxisInterval} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `€${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="Receita" fill="#9333ea" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ── Clientes Novos vs Recorrentes | Método de Pagamento ── */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <p className="font-bold text-gray-900 mb-0.5">Novos vs Recorrentes</p>
            <p className="text-xs text-gray-400 mb-4">
              Recorrente = mais de 1 compra no total · período: {periodLabel}
            </p>
            {stats.customerPieData.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">Sem dados no período</p>
            ) : (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width={140} height={140}>
                  <PieChart>
                    <Pie data={stats.customerPieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={3}>
                      {stats.customerPieData.map((_, i) => (
                        <Cell key={i} fill={i === 0 ? '#3b82f6' : '#ec4899'} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-4 flex-1">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-sm font-semibold text-gray-700">Novos</span>
                    </div>
                    <p className="text-2xl font-extrabold text-gray-900">{stats.newCustomers}</p>
                    <p className="text-xs text-gray-400">€{stats.newRevenue.toFixed(2)} receita</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full bg-pink-500" />
                      <span className="text-sm font-semibold text-gray-700">Recorrentes</span>
                    </div>
                    <p className="text-2xl font-extrabold text-gray-900">{stats.repeatCustomers}</p>
                    <p className="text-xs text-gray-400">€{stats.repeatRevenue.toFixed(2)} receita</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <p className="font-bold text-gray-900 mb-0.5">Método de Pagamento</p>
            <p className="text-xs text-gray-400 mb-4">Encomendas pagas — {periodLabel}</p>
            {stats.methodData.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">Sem dados no período</p>
            ) : (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width={140} height={140}>
                  <PieChart>
                    <Pie data={stats.methodData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={3}>
                      {stats.methodData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3 flex-1">
                  {stats.methodData.map((m, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i] }} />
                          <span className="text-sm font-semibold text-gray-700">{m.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-base font-extrabold text-gray-900">{m.value}</span>
                          {m.revenue > 0 && <span className="text-xs text-gray-400 ml-1">· €{m.revenue.toFixed(2)}</span>}
                        </div>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full mt-1">
                        <div
                          className="h-1.5 rounded-full"
                          style={{
                            width: `${(m.value / stats.periodPaidCount * 100).toFixed(0)}%`,
                            background: COLORS[i],
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Receita por Plataforma | Vendas por Dia da Semana ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <p className="font-bold text-gray-900 mb-0.5">Receita por Plataforma</p>
            <p className="text-xs text-gray-400 mb-4">{periodLabel}</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.platformData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `€${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#374151' }} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Receita" radius={[0, 6, 6, 0]}>
                  {stats.platformData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <p className="font-bold text-gray-900 mb-0.5">Melhores Dias da Semana</p>
            <p className="text-xs text-gray-400 mb-4">Encomendas pagas — {periodLabel}</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.dayData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Encomendas" radius={[6, 6, 0, 0]}>
                  {stats.dayData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Top Clientes | Pacotes Mais Vendidos ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-5 h-5 text-yellow-400" />
              <p className="font-bold text-gray-900">Top Clientes</p>
            </div>
            <p className="text-xs text-gray-400 mb-4">Por receita total acumulada</p>
            <div className="space-y-3">
              {stats.topCustomers.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Sem dados</p>
              ) : stats.topCustomers.map((c, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-300' : i === 2 ? 'bg-orange-400' : 'bg-gray-100'}`}>
                    <span className={i < 3 ? 'text-base' : 'text-gray-600 text-xs font-bold'}>
                      {i < 3 ? ['🥇', '🥈', '🥉'][i] : i + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                    <p className="text-xs text-gray-400 truncate">{c.email}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-purple-600 text-sm">€{c.total.toFixed(2)}</p>
                    <p className="text-xs text-gray-400">{c.count} compra{c.count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingBag className="w-5 h-5 text-purple-500" />
              <p className="font-bold text-gray-900">Pacotes Mais Vendidos</p>
            </div>
            <p className="text-xs text-gray-400 mb-4">Por número de vendas — todos os tempos</p>
            <div className="space-y-3">
              {stats.topPackages.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Sem dados</p>
              ) : stats.topPackages.map((p, i) => {
                const max = stats.topPackages[0].count;
                return (
                  <div key={i}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700 flex-shrink-0">
                        {i + 1}
                      </div>
                      <span className="text-sm font-semibold text-gray-900 flex-1 truncate capitalize">{p.name}</span>
                      <span className="font-bold text-gray-700 text-sm flex-shrink-0">{p.count}×</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full ml-8">
                      <div
                        className="h-1.5 rounded-full bg-purple-400"
                        style={{ width: `${(p.count / max * 100).toFixed(0)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
